import { apiClient } from './client';
import { unwrapList, unwrapItem } from './unwrap';
import type { Organization, OrganizationMember, OrganizationRole, PagedResult } from './types';

/**
 * Must match backend/src/Aperture.Domain/Organizations/OrganizationRole.cs
 * enum order exactly. OrganizationMemberDto.role and
 * AddOrganizationMemberRequest.role are both raw numeric ordinals on the wire
 * (System.Text.Json default, no JsonStringEnumConverter), NOT role name
 * strings - sending a string role to POST /organizations/{id}/members returns 400.
 */
const ORG_ROLE_BY_INDEX: OrganizationRole[] = ['OrganizationAdmin', 'Organizer'];

function roleToIndex(role: OrganizationRole): number {
  return ORG_ROLE_BY_INDEX.indexOf(role);
}

interface RawOrganizationMember {
  id: string;
  organizationId: string;
  userId: string;
  role: number;
}

function normalizeMember(raw: RawOrganizationMember): OrganizationMember {
  return { ...raw, role: ORG_ROLE_BY_INDEX[raw.role] ?? 'Organizer' };
}

export const organizationsApi = {
  list: (params: { page?: number; pageSize?: number; search?: string } = {}) =>
    apiClient
      .get('/organizations', { params })
      .then((r) => unwrapList<Organization>(r.data)) as Promise<PagedResult<Organization>>,

  get: (id: string) =>
    apiClient.get(`/organizations/${id}`).then((r) => unwrapItem<Organization>(r.data)),

  create: (payload: Partial<Organization>) =>
    apiClient.post<Organization>('/organizations', payload).then((r) => r.data),

  update: (id: string, payload: Partial<Organization>) =>
    apiClient.put<Organization>(`/organizations/${id}`, payload).then((r) => r.data),

  /** Multipart upload - matches POST /organizations/{id}/logo (OrganizationsController.UploadLogo). */
  uploadLogo: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<Organization>(`/organizations/${id}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  /** Soft-disable (reversible via the Users/Organizations "activate" flows). SuperAdmin only. */
  deactivate: (id: string) => apiClient.post(`/organizations/${id}/deactivate`).then((r) => r.data),

  /**
   * Permanently deletes the organization AND all of its competitions (which
   * themselves force-delete their submissions/images/scores/awards) - see
   * OrganizationsService.DeleteAsync. Irreversible. SuperAdmin only.
   */
  remove: (id: string) => apiClient.delete(`/organizations/${id}`).then((r) => r.data),

  getMembers: (id: string) =>
    apiClient
      .get(`/organizations/${id}/members`)
      .then((r) => unwrapList<RawOrganizationMember>(r.data).items.map(normalizeMember)),

  addMember: (id: string, payload: { userId: string; role: OrganizationRole }) =>
    apiClient
      .post<RawOrganizationMember>(`/organizations/${id}/members`, {
        userId: payload.userId,
        role: roleToIndex(payload.role),
      })
      .then((r) => normalizeMember(r.data)),

  removeMember: (id: string, userId: string) =>
    apiClient.delete(`/organizations/${id}/members/${userId}`).then((r) => r.data),
};
